import { Trans, useTranslation } from 'react-i18next';
import { backButton, miniApp, openInvoice } from '@telegram-apps/sdk-react';
import { useEffect, useMemo, useState } from 'react';
import type { InvoiceStatus } from '@telegram-apps/bridge';

import {
  wrapperStyle,
  topStarContainerStyle,
  topCirclesWrapperStyle,
  circle240Style,
  circle180Style,
  darkBlockWrapperStyle,
  darkStarWrapperStyle,
  darkStarOuterCircleStyle,
  darkStarInnerCircleStyle,
  titleBlockStyle,
  titleTextStyle,
  subtitleTextStyle,
  starInlineStyle,
  mainButtonStyleStyle
} from './styles';
import Slider from './Slider';

import { useHeaderStore } from '@/store/useHeadersStylesStore';
import UIConfig from '@/UIConfig';
import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal';
import { TelegramCustomStar } from '@/components/common/svg/telegram/TelegramCustomStar';
import { MainButton } from '@/components/common/MainButton/MainButton';
import SmallStarsShape from '@/components/common/svg/shapes/SmallStarsShape';
import PurchaseController from '@/api/purchases/purchases';
import { catchErrors } from '@/utils/sentry-monitor';
import { useProfileStore } from '@/store/useProfileStore';
import { OFFERING_IDS } from '@/constants/offering_ids';
import { ProductInvoicePlacements } from '@/enums/apiEnums';

type PremiumUpsalePopupProps = {
  handleClose: () => void;
};

const PremiumUpsalePopup = ({ handleClose }: PremiumUpsalePopupProps) => {
  const { t } = useTranslation();
  const { setHeaderColors, switchHeaderColors } = useHeaderStore();
  const { aboutMe } = useProfileStore();

  const premium7 = useMemo(
    () =>
      aboutMe?.billing_offerings
        ?.find((o) => o.id === OFFERING_IDS.premiumId)
        ?.package_deals?.find(
          (d) => d.product.subscription_details?.service_duration_days === 7
        ),
    [aboutMe]
  );

  if (!premium7) {
    handleClose();
    return;
  }

  useBackButtonToCloseModal(handleClose, true);

  const [isProcessing, setIsProcessing] = useState(false);

  const createInvoice = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const res = await PurchaseController.createInvoice({
        package_deal_id: premium7.id,
        product_placement_name:
          ProductInvoicePlacements.MATCHMAKER_PREMIUM_UPSALE_POPUP
      });

      const status: InvoiceStatus = await openInvoice(res.invoice_url, 'url');

      if (status === 'paid') {
        if (backButton.isVisible()) backButton.hide();
        handleClose();
      }
    } catch (e) {
      catchErrors(e, true);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const original = miniApp.bottomBarColor();
    miniApp.setBottomBarColor('#1D1D1D');

    setHeaderColors({
      backgroundColor: '#B676E5',
      logoColor: UIConfig.Colors.BackgroundWhite
    });

    return () => {
      switchHeaderColors('dark');
      miniApp.setBottomBarColor(original);
    };
  }, []);

  return (
    <div style={wrapperStyle}>
      <div style={topStarContainerStyle}>
        <SmallStarsShape
          height={48}
          width={48}
          style={{ transform: 'rotate(-70deg)' }}
        />
      </div>

      <div style={topCirclesWrapperStyle}>
        <div style={circle240Style} />
        <div style={circle180Style} />
      </div>

      <div style={darkBlockWrapperStyle}>
        <div style={darkStarWrapperStyle}>
          <div style={darkStarOuterCircleStyle} />
          <div style={darkStarInnerCircleStyle}>
            <TelegramCustomStar height={50} width={50} fillColor="#fff" />
          </div>
        </div>

        <div style={titleBlockStyle}>
          <span style={titleTextStyle}>{t('paywall.title.premium')}</span>

          <p style={subtitleTextStyle}>
            <Trans
              i18nKey="paywall.subtitle.premium"
              components={{ br: <br />, bold: <b /> }}
            />
          </p>
        </div>

        <Slider />

        <MainButton
          fixed
          text={
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap'
              }}
            >
              {t('button.get_premium')}
              <TelegramCustomStar
                style={starInlineStyle}
                height={17}
                width={17}
                fillColor="#fff"
              />
              {premium7.amount}
              &nbsp;{t('button.get_premium_weekly')}
            </span>
          }
          buttonStyle={mainButtonStyleStyle}
          onClick={createInvoice}
          disabled={isProcessing}
          loading={isProcessing}
        />
      </div>
    </div>
  );
};

export default PremiumUpsalePopup;
